
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as entity from '../entity'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {ChartBar} from '../core/ChartBar';
import {FieldModelChartBar} from '../core/ChartBar';

/**
 * ChartBarModifyNotify struct
 */
class ChartBarModifyNotify {
  /**
   * Initialize struct
   * @param {!ChartBar=} chartBar
   * @constructor
   */
  constructor (chartBar = new ChartBar()) {
    this.ChartBar = chartBar
  }

  /**
   * Copy struct (shallow copy)
   * @this {!ChartBarModifyNotify}
   * @param {!ChartBarModifyNotify} other Other struct
   * @returns {!ChartBarModifyNotify} This struct
   */
  copy (other) {
    if (other.ChartBar != null) {
      this.ChartBar = ChartBar.fromObject(other.ChartBar)
    } else {
      this.ChartBar = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!ChartBarModifyNotify}
   * @returns {!ChartBarModifyNotify} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new ChartBarModifyNotifyModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new ChartBarModifyNotifyModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!ChartBarModifyNotify}
   * @param {!ChartBarModifyNotify} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof ChartBarModifyNotify)) {
      throw new TypeError('Instance of ChartBarModifyNotify is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!ChartBarModifyNotify}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      ChartBar: ((this.ChartBar != null) ? this.ChartBar : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return ChartBarModifyNotify.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!ChartBarModifyNotify} other Object value
   * @returns {!ChartBarModifyNotify} Created struct
   */
  static fromObject (other) {
    return new ChartBarModifyNotify().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!ChartBarModifyNotify}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return ChartBarModifyNotify.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!ChartBarModifyNotify}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 3013
  }
}

export { ChartBarModifyNotify };

/**
 * Fast Binary Encoding ChartBarModifyNotify field model
 */
class FieldModelChartBarModifyNotify extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.ChartBar = new FieldModelChartBar(buffer, 4 + 4)
  }

  /**
   * Get the field size
   * @this {!FieldModelChartBarModifyNotify}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelChartBarModifyNotify}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.ChartBar.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelChartBarModifyNotify}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.ChartBar.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelChartBarModifyNotify}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelChartBarModifyNotify.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelChartBarModifyNotify}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 3013
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelChartBarModifyNotify}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelChartBarModifyNotify}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.ChartBar.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.ChartBar.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ChartBar.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelChartBarModifyNotify}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelChartBarModifyNotify}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelChartBarModifyNotify}
   * @param {!ChartBarModifyNotify} fbeValue Default value, defaults is new ChartBarModifyNotify()
   * @returns {!ChartBarModifyNotify} ChartBarModifyNotify value
   */
  get (fbeValue = new ChartBarModifyNotify()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelChartBarModifyNotify}
   * @param {!ChartBarModifyNotify} fbeValue ChartBarModifyNotify value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.ChartBar.fbeSize) <= fbeStructSize) {
      fbeValue.ChartBar = this.ChartBar.get()
    } else {
      fbeValue.ChartBar = new ChartBar()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ChartBar.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelChartBarModifyNotify}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelChartBarModifyNotify}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelChartBarModifyNotify}
   * @param {!ChartBarModifyNotify} fbeValue ChartBarModifyNotify value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelChartBarModifyNotify}
   * @param {ChartBarModifyNotify} fbeValue ChartBarModifyNotify value
   */
  setFields (fbeValue) {
    this.ChartBar.set(fbeValue.ChartBar)
  }
}

export { FieldModelChartBarModifyNotify };

/**
 * Fast Binary Encoding ChartBarModifyNotify model
 */
class ChartBarModifyNotifyModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelChartBarModifyNotify(this.buffer, 4)
  }

  /**
   * Get the ChartBarModifyNotify model
   * @this {!ChartBarModifyNotifyModel}
   * @returns {!FieldModelChartBarModifyNotify} model ChartBarModifyNotify model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!ChartBarModifyNotifyModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!ChartBarModifyNotifyModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return ChartBarModifyNotifyModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!ChartBarModifyNotifyModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelChartBarModifyNotify.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!ChartBarModifyNotifyModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!ChartBarModifyNotifyModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!ChartBarModifyNotifyModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!ChartBarModifyNotifyModel}
   * @param {!ChartBarModifyNotify} value ChartBarModifyNotify value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!ChartBarModifyNotifyModel}
   * @param {!ChartBarModifyNotify} value ChartBarModifyNotify value, defaults is new ChartBarModifyNotify()
   * @return {!object} Deserialized ChartBarModifyNotify value and its size
   */
  deserialize (value = new ChartBarModifyNotify()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new ChartBarModifyNotify(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new ChartBarModifyNotify(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!ChartBarModifyNotifyModel}
   * @param {!number} prev Previous ChartBarModifyNotify model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { ChartBarModifyNotifyModel };
